import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, FileText, Users, Calendar, TrendingUp, ArrowUpRight, AlertTriangle, CreditCard, MessageSquare, FileSpreadsheet, Bell } from "lucide-react";
import { http } from "../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    http.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    http.get("/admin/cotizaciones/comunidad").then((r) => setRecent((r.data || []).slice(0, 5))).catch(() => {});
  }, []);

  const mensajes_nuevos = stats?.mensajes_nuevos || 0;
  const formularios_nuevos = stats?.formularios_nuevos || 0;
  const pendientes = mensajes_nuevos + formularios_nuevos;

  const cards = [
    { label: "Formularios nuevos", value: stats?.formularios_nuevos ?? "—", icon: FileSpreadsheet, color: "var(--brand-accent)", to: "/admin/formularios-web" },
    { label: "Mensajes nuevos", value: stats?.mensajes_nuevos ?? "—", icon: MessageSquare, color: "var(--brand-accent)", to: "/admin/mensajes" },
    { label: "Cartas asignación", value: stats?.cartas_asignacion ?? "—", icon: FileText, color: "var(--brand-navy)", to: "/admin/cartas" },
    { label: "Cotizaciones", value: stats?.cotizaciones_nuevas ?? "—", icon: Inbox, color: "var(--brand-navy)", to: "/admin/cotizaciones" },
    { label: "Clientes activos", value: stats?.clientes ?? "—", icon: Users, color: "#2E7D32", to: "/admin/clientes" },
    { label: "Renovaciones 30d", value: stats?.renovaciones_30d ?? "—", icon: Calendar, color: "#F57C00", to: "/admin/renovaciones" },
    { label: "Cobros pendientes", value: stats?.cobros_pendientes ?? "—", icon: CreditCard, color: "#D32F2F", to: "/admin/cobranza" },
    { label: "Siniestros activos", value: stats?.siniestros_activos ?? "—", icon: AlertTriangle, color: "#F57C00", to: "/admin/siniestros" },
  ];

  return (
    <div className="fade-in-up" data-testid="admin-dashboard">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: "var(--brand-accent)" }}>Panel administrativo</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Resumen operativo</h1>
      </div>

      {pendientes > 0 && (
        <div
          className="mb-6 p-4 md:p-5 rounded-xl flex flex-col sm:flex-row sm:items-center gap-4 border"
          style={{ background: "rgba(238,81,71,0.05)", borderColor: "rgba(238,81,71,0.25)" }}
          data-testid="pending-banner"
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "var(--brand-accent)" }}>
            <Bell size={18} color="white"/>
          </div>
          <div className="flex-1">
            <div className="font-bold text-[15px]" style={{ fontFamily: "Manrope" }}>
              Tienes {pendientes} {pendientes === 1 ? "envío" : "envíos"} sin atender
            </div>
            <div className="text-[13px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
              {formularios_nuevos > 0 && (<>
                <Link to="/admin/formularios-web" className="font-semibold underline" style={{ color: "var(--brand-accent)" }}>
                  {formularios_nuevos} {formularios_nuevos === 1 ? "formulario" : "formularios"}
                </Link>
              </>)}
              {formularios_nuevos > 0 && mensajes_nuevos > 0 && " · "}
              {mensajes_nuevos > 0 && (<>
                <Link to="/admin/mensajes" className="font-semibold underline" style={{ color: "var(--brand-accent)" }}>
                  {mensajes_nuevos} {mensajes_nuevos === 1 ? "mensaje" : "mensajes"}
                </Link>
              </>)}
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c, i) => (
          <Link to={c.to} key={i} className="p-5 bg-white border rounded-xl hover:shadow-md transition" style={{ borderColor: "var(--border-subtle)" }} data-testid={`stat-card-${i}`}>
            <div className="flex items-start justify-between mb-5">
              <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: c.color + "22" }}>
                <c.icon size={18} style={{ color: c.color }}/>
              </div>
              <ArrowUpRight size={16} style={{ color: "var(--text-muted)" }}/>
            </div>
            <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{c.value}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border rounded-xl" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Últimas cotizaciones comunidad</h3>
              <p className="text-xs" style={{ color: "var(--text-muted)" }}>Las 5 solicitudes más recientes</p>
            </div>
            <Link to="/admin/cotizaciones" className="text-sm font-semibold nav-link" style={{ color: "var(--brand-accent)" }}>Ver todas →</Link>
          </div>
          {recent.length === 0 ? (
            <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>No hay cotizaciones aún.</div>
          ) : (
            <table className="tbl">
              <thead>
                <tr><th>Comunidad</th><th>Contacto</th><th>UF</th><th>Estado</th></tr>
              </thead>
              <tbody>
                {recent.map((c, i) => (
                  <tr key={c.id || i}>
                    <td className="font-semibold">{c.razon_social}</td>
                    <td>{c.nombre} {c.apellido}</td>
                    <td>{c.prima_estimada_uf ? `UF ${c.prima_estimada_uf.toFixed(1)}` : "—"}</td>
                    <td><span className={`badge badge--${c.estado || "nueva"}`}>{c.estado || "nueva"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border rounded-xl p-6" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp size={18} style={{ color: "var(--brand-accent)" }}/>
            <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Estado general</h3>
          </div>
          <div className="space-y-4">
            <StatRow label="Cotizaciones comunidad" value={stats?.cotizaciones_comunidad ?? "—"}/>
            <StatRow label="Cotizaciones residentes" value={stats?.cotizaciones_residente ?? "—"}/>
            <StatRow label="Pólizas activas" value={stats?.polizas_activas ?? "—"}/>
          </div>
        </div>
      </div>
    </div>
  );
}

const StatRow = ({ label, value }) => (
  <div className="flex items-center justify-between pb-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
    <span className="text-sm" style={{ color: "var(--text-muted)" }}>{label}</span>
    <span className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{value}</span>
  </div>
);
